import { CSSObject } from '@emotion/react';
import _css, { SystemStyleObject } from '@styled-system/css';
import SSShouldForwardProp from '@styled-system/should-forward-prop';
import {
  ColorProps as SSColorProps,
  get,
  styleFn,
  Theme as StyledSystemTheme,
} from 'styled-system';

export type CoreVariantGroupNames =
  | 'anchors'
  | 'buttons'
  | 'divs'
  | 'footers'
  | 'headings'
  | 'headers'
  | 'images'
  | 'inputs'
  | 'labels'
  | 'lists'
  | 'orderedLists'
  | 'unorderedLists'
  | 'listItems'
  | 'navigations'
  | 'paragraphs'
  | 'sections'
  | 'selects'
  | 'spans'
  | 'svgs'
  | 'tables'
  | 'tableCells'
  | 'tableRows';

export type Variant = {
  [variantName: string]: SystemStyleObject | string | number;
};
export type Variants<VariantGroupNames extends string> = {
  [K in VariantGroupNames]: Variant;
};
export type CoreVariants = Variants<CoreVariantGroupNames>;
export interface CoreTheme<V extends string | Variants<any> = CoreVariants>
  extends StyledSystemTheme {
  breakpoints: Array<number | string>;
  variants: Partial<(V extends string ? Variants<V> : V) & CoreVariants>;
  global?: CSSObject;
}

export interface WithVariant {
  variant?: string;
}

export interface CoreBaseProps extends WithVariant {
  css?: SystemStyleObject;
}

export interface ColorProps extends SSColorProps {
  color?: SSColorProps['color'] & string;
}

export type CollidingProps<
  Props,
  E extends keyof JSX.IntrinsicElements
> = keyof Props & keyof JSX.IntrinsicElements[E];

export type WithoutCollidingProps<
  BaseProps,
  E extends keyof JSX.IntrinsicElements
> = Omit<JSX.IntrinsicElements[E], CollidingProps<BaseProps, E>>;

export const shouldForwardProp = <Props>(
  propName: keyof Props
): propName is keyof Props => SSShouldForwardProp(propName as string);

/**
 * Based on code from https://github.com/bruceragon/themed-components
 */

interface Config {
  defaultVariantKey?: string;
  variantsGlobalStyleKey?: string;
}

const defaultConfig: Config = {
  defaultVariantKey: '_default',
  variantsGlobalStyleKey: '_all',
};

export function defineWithTheme(scale: string, config?: Config) {
  return ({ css, theme, variant }: any): '' | styleFn => {
    if (!theme) {
      return '';
    }
    config = { ...defaultConfig, ...config };
    const { defaultVariantKey, variantsGlobalStyleKey } = config;

    const variantAsArray: string[] = variant ? variant.split('.') : [];
    const variantHasScale = variantAsArray.length > 1;
    const _scale = variantHasScale ? variantAsArray[0] : scale;
    const variantWithoutScale: string | undefined = variantHasScale
      ? variantAsArray.slice(1).join('.')
      : variant;

    // if variant is empty => use _default
    const _variant = variantWithoutScale ?? defaultVariantKey;
    const variantsGlobalStyle =
      get(theme, `variants.${_scale}.${variantsGlobalStyleKey}`) ?? {};

    const styleObject = get(theme, `variants.${_scale}.${_variant}`) ?? {};
    return _css({ ...variantsGlobalStyle, ...styleObject, ...css }) as styleFn;
  };
}
